import { Link, useLocation } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { openConsentSettings } from '@/lib/consent';
const logoLight = '/scuvio-logo-light.svg';

const Footer = () => {
  const { t } = useI18n();
  const location = useLocation();

  if (location.pathname.startsWith('/admin')) return null;

  const sections = [
    {
      title: t('footer.product'),
      links: [
        { label: t('nav.guide'), to: '/guide' },
        { label: t('nav.map'), to: '/map' },
        { label: t('nav.visibility'), to: '/visibility' },
        { label: t('nav.planner'), to: '/planner' },
      ],
    },
    {
      title: t('footer.platform'),
      links: [
        { label: t('footer.aboutUs'), to: '/about' },
        { label: t('footer.vision'), to: '/vision' },
        { label: t('footer.partners'), to: '/our-partners' },
        { label: 'Scuvio unterstützen', to: '/support' },
      ],
    },
    {
      title: t('footer.community'),
      links: [
        { label: t('footer.diveClubs'), to: '/dive-clubs' },
        { label: t('footer.diveSchools'), to: '/dive-schools' },
        { label: t('footer.becomePartner'), to: '/become-partner' },
        { label: t('footer.submitSpot'), to: '/submit-spot' },
      ],
    },
    {
      title: t('footer.legal'),
      links: [
        { label: t('footer.imprint'), to: '/impressum' },
        { label: t('footer.privacyPolicy'), to: '/datenschutz' },
        { label: t('footer.termsOfService'), to: '/nutzungsbedingungen' },
        { label: t('footer.contactUs'), to: '/contact' },
      ],
    },
  ];

  return (
    <footer
      className="relative hidden lg:block bg-[hsl(var(--abyss))] text-[hsl(var(--salt-white))] pb-bottom-nav lg:pb-0"
    >
      <div className="h-px bg-gradient-to-r from-transparent via-[hsl(var(--pelagic)/0.4)] to-transparent" />

      <div className="max-w-6xl mx-auto px-5 sm:px-6 pt-10 lg:pt-12 pb-8 lg:pb-10">
        {/* Brand block — compact on mobile/tablet, full column on desktop. */}
        <div className="lg:grid lg:grid-cols-5 lg:gap-6">
          <div className="lg:col-span-1 lg:pr-4 mb-8 lg:mb-0">
            <Link to="/" className="inline-flex items-center gap-2 group">
              <img src={logoLight} alt="Scuvio" className="h-10 lg:h-12 w-auto" />
            </Link>
            <p className="mt-3 text-sm leading-relaxed text-[hsl(var(--sand-grey))]">
              {t('footer.tagline')}
            </p>
          </div>

          {/* Link columns. Mobile: 2-col; Tablet: 4-col; Desktop: 4-col aligned. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-y-8 gap-x-6 lg:col-span-4">
            {sections.map((section) => (
              <div key={section.title}>
                <h4 className="text-[11px] font-semibold uppercase tracking-wider text-[hsl(var(--sand-grey))] mb-3">
                  {section.title}
                </h4>
                <ul className="space-y-2.5 sm:space-y-2">
                  {section.links.map((link) => (
                    <li key={link.to + link.label}>
                      <Link
                        to={link.to}
                        className="block py-0.5 text-sm text-[hsl(var(--sand-grey)/0.85)] hover:text-[hsl(var(--salt-white))] active:text-[hsl(var(--salt-white))] transition-colors duration-200"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-10 pt-6 border-t border-[hsl(var(--pelagic)/0.15)] flex flex-col sm:flex-row items-center sm:items-center justify-between gap-3 text-center sm:text-left">
          <p className="text-xs text-[hsl(var(--sand-grey)/0.6)]">
            © {new Date().getFullYear()} scuvio · Explore. Dive. Rate.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5">
            <button
              type="button"
              onClick={openConsentSettings}
              className="text-xs text-[hsl(var(--sand-grey)/0.5)] hover:text-[hsl(var(--sand-grey))] transition-colors"
            >
              {t('footer.cookieSettings')}
            </button>
            <Link to="/datenschutz" className="text-xs text-[hsl(var(--sand-grey)/0.5)] hover:text-[hsl(var(--sand-grey))] transition-colors">
              {t('footer.privacyPolicy')}
            </Link>
            <Link to="/impressum" className="text-xs text-[hsl(var(--sand-grey)/0.5)] hover:text-[hsl(var(--sand-grey))] transition-colors">
              {t('footer.imprint')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
